import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRoomView, joinRoom } from './api';
import { Landing } from './screens/Landing';
import { CreateJoin } from './screens/CreateJoin';
import { Lobby } from './screens/Lobby';
import { GameScreen } from './screens/GameScreen';
import { GameOver } from './screens/GameOver';
import { Loading } from './components/Loading';
import { ErrorBanner } from './components/ErrorBanner';
import { HelpModal } from './components/HelpModal';
import { clearSession, loadSession, saveSession, type StoredSession } from './storage';
import { codeFromLocation } from './url';
import { useRoomSocket } from './useRoomSocket';
import { useEffectiveReducedMotion } from './useReducedMotion';

type View = 'landing' | 'createjoin';

/** Top-level screen router. Owns the one (or two, for local-2P) WebSocket
 * connection(s) and derives which screen to show from server-reported
 * `status` + whether a round result has arrived -- never from a
 * client-guessed phase (SPEC.md §1.1). */
export function App(): JSX.Element {
  const [view, setView] = useState<View>('landing');
  const [session, setSession] = useState<StoredSession | null>(null);
  const [localP2, setLocalP2] = useState<StoredSession | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // PART 5a: same optimistic-pending pattern as GameOver.tsx's own local
  // state, for the "stale round" fallback block below (a late reconnect
  // straight into an already-finished room, which never got the one-shot
  // 'over' broadcast -- see that block's own comment). Local UI state only;
  // this screen unmounts once the authoritative `lobby` broadcast arrives.
  const [staleRematchPending, setStaleRematchPending] = useState(false);
  // PART 6: "How to play" reachable from the Lobby, not just Landing/Pause.
  const [showLobbyHelp, setShowLobbyHelp] = useState(false);

  const initialCode = useMemo(() => codeFromLocation(), []);

  // PART 4: manual reduced-motion override ('auto' follows the OS query,
  // 'on'/'off' force it either way) applied at the document root so every
  // CSS rule already gated on `@media (prefers-reduced-motion: reduce)`
  // (spinner/btn/joystick-knob/level-clear-label transitions -- see
  // index.css) ALSO reacts to a manual 'on', even when the OS itself
  // reports no-preference (see index.css's `.reduced-motion` mirror block).
  const effectiveReducedMotion = useEffectiveReducedMotion();
  useEffect(() => {
    document.body.classList.toggle('reduced-motion', effectiveReducedMotion);
  }, [effectiveReducedMotion]);

  // Restore-from-storage (SPEC.md §4: fresh browser + code/token still sees
  // the same room/seats/scores/lives). Validate the room still exists
  // before committing to it -- an unreachable/expired room falls back to
  // the landing screen with a clear error, instead of spinning forever on
  // a WebSocket that will never open.
  useEffect(() => {
    const stored = loadSession();
    if (!stored) {
      setRestoring(false);
      return;
    }
    getRoomView(stored.code)
      .then(() => {
        setSession(stored);
        setRestoring(false);
      })
      .catch(() => {
        clearSession();
        setRestoreError('Your previous room is no longer available.');
        setRestoring(false);
      });
  }, []);

  const socket = useRoomSocket(session?.code ?? null, session?.token ?? null);
  const p2Socket = useRoomSocket(localP2 ? session?.code ?? null : null, localP2?.token ?? null);

  const handleJoined = useCallback((s: StoredSession) => {
    saveSession(s);
    setSession(s);
    setRestoreError(null);
  }, []);

  const handleLeave = useCallback(() => {
    socket.sendLeave(); // free our seat server-side so a new friend can join
    clearSession();
    setSession(null);
    setLocalP2(null);
    setView('landing');
  }, [socket]);

  // PART 1: keep the locally-stored session's `name` in sync with the
  // authoritative lobby broadcast -- never written optimistically at
  // rename-request time (SPEC.md §1.1), only once the server's own `lobby`
  // message confirms it, so a refresh right after a rename still restores
  // the NEW name instead of the stale one baked in at join time.
  useEffect(() => {
    if (!session || !socket.lobby) return;
    const mine = socket.lobby.seats[session.seat - 1];
    if (mine?.name && mine.name !== session.name) {
      const updated: StoredSession = { ...session, name: mine.name };
      saveSession(updated);
      setSession(updated);
    }
  }, [socket.lobby, session]);

  const handleStartLocalP2 = useCallback(async () => {
    if (!session) return;
    try {
      const res = await joinRoom(session.code, 'Player 2 (this device)');
      setLocalP2({ code: session.code, token: res.player_token, seat: 2, name: 'Player 2 (this device)' });
    } catch {
      // Best-effort convenience feature; if seat 2 is already taken (a real
      // second player joined first) we simply don't offer local control --
      // the room itself is unaffected.
    }
  }, [session]);

  if (restoring) {
    return (
      <div className="app-shell">
        <Loading label="Loading PacMates..." />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app-shell">
        {restoreError && <ErrorBanner message={restoreError} onDismiss={() => setRestoreError(null)} />}
        {view === 'landing' ? (
          <Landing onPlay={() => setView('createjoin')} />
        ) : (
          <CreateJoin initialCode={initialCode} onJoined={handleJoined} onBack={() => setView('landing')} />
        )}
      </div>
    );
  }

  const canRematch = socket.connStatus === 'open';

  if (socket.roundResult) {
    return (
      <div className="app-shell">
        {socket.lastError && <ErrorBanner message={socket.lastError} onDismiss={socket.clearError} />}
        <GameOver result={socket.roundResult} onPlayAgain={socket.sendRematch} onLeave={handleLeave} canRematch={canRematch} />
      </div>
    );
  }

  const status = socket.lobby?.status ?? 'lobby';

  // A late reconnect straight into an already-finished room never gets the
  // one-shot 'over' broadcast (it already fired before this socket opened)
  // and never gets a 'playing'/'paused' snapshot either (see
  // wsServer.ts onConnection) -- without this fallback the screen would
  // spin on "Loading game..." forever.
  if (status === 'over' && !socket.roundResult) {
    return (
      <div className="app-shell">
        {socket.lastError && <ErrorBanner message={socket.lastError} onDismiss={socket.clearError} />}
        <div className="screen gameover-screen" data-testid="screen-gameover-stale">
          <h1 className="title-sans">Round already ended</h1>
          <p className="dim">This room's round finished before you connected.</p>
          {!canRematch && <p className="dim">Reconnecting... you can play again once you're back online.</p>}
          <div className="gameover-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setStaleRematchPending(true);
                socket.sendRematch();
              }}
              disabled={!canRematch || staleRematchPending}
              autoFocus
              data-testid="btn-play-again"
            >
              {staleRematchPending ? 'Returning to lobby...' : 'Play again with the same team'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleLeave} data-testid="btn-leave">
              Leave
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'lobby') {
    return (
      <div className="app-shell">
        {socket.lastError && <ErrorBanner message={socket.lastError} onDismiss={socket.clearError} />}
        <Lobby
          session={session}
          lobby={socket.lobby}
          connStatus={socket.connStatus}
          onToggleReady={socket.sendReady}
          onStartSolo={socket.sendStart}
          onLeave={handleLeave}
          onRename={socket.sendRename}
          showLocal2pHint={session.seat === 1 && !localP2 && !!socket.lobby && !socket.lobby.seats[1]!.name}
          onStartLocalP2={handleStartLocalP2}
          onOpenHelp={() => setShowLobbyHelp(true)}
        />
        {showLobbyHelp && <HelpModal onClose={() => setShowLobbyHelp(false)} />}
      </div>
    );
  }

  return (
    <div className="app-shell">
      {socket.lastError && <ErrorBanner message={socket.lastError} onDismiss={socket.clearError} />}
      <GameScreen
        session={session}
        lobby={socket.lobby}
        status={status}
        state={socket.snapshot?.state ?? null}
        prevState={socket.prevSnapshot?.state ?? null}
        receivedAt={socket.snapshot?.receivedAt ?? performance.now()}
        connStatus={socket.connStatus}
        onSendInput={socket.sendInput}
        onSendInputP2={localP2 ? p2Socket.sendInput : undefined}
        onPause={socket.sendPause}
        onResume={socket.sendResume}
        onLeave={handleLeave}
        localTwoPlayer={!!localP2}
      />
    </div>
  );
}

